import type {
  MCPAction,
  ToolHeaderValueType,
  ToolParamDataType,
  ToolParamSchemaItem,
  ToolParamValueType,
  ToolRequestHeader,
} from '@/types/tool'

export type HeaderDraft = {
  id: string
  name: string
  value_type: ToolHeaderValueType
  value: string
}

export type ParamDraft = {
  draft_id: string
  id: string
  type: ToolParamDataType
  value_type: ToolParamValueType
  description: string
  required: boolean
  enum: string[]
  value: string
}

/** Path/query param types offered in Tool Studio (excludes array/object). */
const PARAM_TYPES: ToolParamDataType[] = ['string', 'number', 'integer', 'boolean']
const PARAM_VALUE_TYPES: ToolParamValueType[] = ['llm_prompt', 'constant', 'dynamic_variable']
const QUERY_HTTP_METHODS = ['GET', 'DELETE'] as const

const HEADER_TYPES: ToolHeaderValueType[] = ['valor', 'variable_dinamica', 'secreto']

function normalizeHeaderValueType(rawHeader: Record<string, unknown>): ToolHeaderValueType {
  const rawValueType = String(rawHeader.value_type || '').trim()
  if (HEADER_TYPES.includes(rawValueType as ToolHeaderValueType)) {
    return rawValueType as ToolHeaderValueType
  }

  const rawType = String(rawHeader.type || '').trim().toLowerCase()
  if (rawType === 'secret') return 'secreto'
  if (rawType === 'dynamic_variable') return 'variable_dinamica'
  if (rawType === 'static') return 'valor'
  return 'valor'
}

export function createHeaderDraft(overrides?: Partial<HeaderDraft>): HeaderDraft {
  return {
    id: `hdr_${Math.random().toString(36).slice(2, 10)}`,
    name: '',
    value_type: 'valor',
    value: '',
    ...overrides,
  }
}

export function createParamDraft(overrides?: Partial<ParamDraft>): ParamDraft {
  return {
    draft_id: `param_draft_${Math.random().toString(36).slice(2, 10)}`,
    id: '',
    type: 'string',
    value_type: 'llm_prompt',
    description: '',
    required: false,
    enum: [],
    value: '',
    ...overrides,
  }
}

export function sanitizeEnumValues(values: unknown): string[] {
  if (!Array.isArray(values)) return []
  const normalized: string[] = []
  values.forEach((value) => {
    if (typeof value !== 'string') return
    const token = value.trim()
    if (!token) return
    if (normalized.includes(token)) return
    normalized.push(token)
  })
  return normalized
}

export function mergeParamDraftsById(...groups: ParamDraft[][]): ParamDraft[] {
  const merged: ParamDraft[] = []
  const seen = new Set<string>()
  groups.forEach((group) => {
    group.forEach((param) => {
      const key = param.id.trim().toLowerCase()
      if (!key || seen.has(key)) return
      seen.add(key)
      merged.push(param)
    })
  })
  return merged
}

type WebhookMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export function methodUsesQueryParams(method: string | undefined): boolean {
  return QUERY_HTTP_METHODS.includes(String(method || '').toUpperCase() as (typeof QUERY_HTTP_METHODS)[number])
}

export function normalizeWebhookMethod(value: unknown): WebhookMethod {
  if (typeof value !== 'string') return 'GET'
  const upper = value.toUpperCase()
  if (upper === 'GET' || upper === 'POST' || upper === 'PUT' || upper === 'PATCH' || upper === 'DELETE') {
    return upper
  }
  return 'GET'
}

export function buildWebhookSchemasFromStudioDrafts(
  method: WebhookMethod,
  pathParams: ParamDraft[],
  transportParams: ParamDraft[],
): {
  path_params_schema: ToolParamSchemaItem[]
  query_params_schema: ToolParamSchemaItem[]
  request_body_schema: ToolParamSchemaItem[]
} {
  const pathPayload = toParamSchemaPayload(pathParams)
  const transportPayload = toParamSchemaPayload(transportParams)
  if (methodUsesQueryParams(method)) {
    return {
      path_params_schema: pathPayload,
      query_params_schema: transportPayload,
      request_body_schema: [],
    }
  }
  return {
    path_params_schema: pathPayload,
    query_params_schema: [],
    request_body_schema: transportPayload,
  }
}

export function parseStudioDraftsFromWebhookSchemas(
  method: WebhookMethod,
  pathSchema: unknown,
  querySchema: unknown,
  requestBodySchema: unknown,
): {
  path_params_schema: ParamDraft[]
  query_params_schema: ParamDraft[]
} {
  const pathDrafts = normalizeParamDrafts(pathSchema)
  const queryDrafts = normalizeParamDrafts(querySchema)
  const requestBodyDrafts = normalizeParamDrafts(requestBodySchema)
  return {
    path_params_schema: pathDrafts,
    query_params_schema: methodUsesQueryParams(method)
      ? mergeParamDraftsById(queryDrafts, requestBodyDrafts)
      : mergeParamDraftsById(requestBodyDrafts, queryDrafts),
  }
}

/** Split URL into the part before ?/# (path + host) and the query+fragment suffix. */
export function splitUrlForPathPlaceholderEdit(url: string): { pathPart: string; rest: string } {
  const trimmed = url.trim()
  if (!trimmed) return { pathPart: '', rest: '' }
  const q = trimmed.indexOf('?')
  const h = trimmed.indexOf('#')
  let cut = trimmed.length
  if (q !== -1) cut = Math.min(cut, q)
  if (h !== -1) cut = Math.min(cut, h)
  return {
    pathPart: trimmed.slice(0, cut),
    rest: trimmed.slice(cut),
  }
}

/**
 * Append `/{paramId}` to the URL path if `{paramId}` is not already present.
 * Keeps query string and fragment intact.
 */
export function ensurePathPlaceholderInUrl(url: string, paramId: string): string {
  const id = paramId.trim()
  if (!id) return url
  const token = `{${id}}`
  if (url.includes(token)) return url
  const trimmed = url.trim()
  if (!trimmed) return url
  const { pathPart, rest } = splitUrlForPathPlaceholderEdit(trimmed)
  if (!pathPart) return url
  const base = pathPart.replace(/\/$/, '')
  return `${base}/${token}${rest}`
}

/** Replace `{oldId}` with `{newId}` in the full URL string. */
export function renamePathPlaceholderInUrl(url: string, oldId: string, newId: string): string {
  const o = oldId.trim()
  const n = newId.trim()
  if (!o || !n || o === n) return url
  return url.split(`{${o}}`).join(`{${n}}`)
}

/** Remove `{paramId}` and collapse duplicate slashes in the path (not the scheme). */
export function removePathPlaceholderFromUrl(url: string, paramId: string): string {
  const id = paramId.trim()
  if (!id || !url.includes(`{${id}}`)) return url
  let next = url.split(`{${id}}`).join('')
  next = next.replace(/([^:]\/)\/+/g, '$1')
  return next
}

/**
 * Extract path placeholder ids present in URL path.
 * Supports both `/items/{id}` and `/items/:id` syntaxes.
 */
export function extractPathPlaceholderIdsFromUrl(url: string): string[] {
  const { pathPart } = splitUrlForPathPlaceholderEdit(url)
  if (!pathPart) return []

  const found: string[] = []
  const seen = new Set<string>()
  const add = (value: string) => {
    const id = value.trim()
    if (!id || seen.has(id)) return
    seen.add(id)
    found.push(id)
  }

  const braceRegex = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g
  let braceMatch = braceRegex.exec(pathPart)
  while (braceMatch) {
    add(braceMatch[1])
    braceMatch = braceRegex.exec(pathPart)
  }

  const colonRegex = /\/:([A-Za-z_][A-Za-z0-9_]*)/g
  let colonMatch = colonRegex.exec(pathPart)
  while (colonMatch) {
    add(colonMatch[1])
    colonMatch = colonRegex.exec(pathPart)
  }

  return found
}

/**
 * Keep path params strictly in sync with placeholders present in URL path.
 * - Existing params keep their metadata when placeholder remains.
 * - Missing placeholders create new draft params.
 * - Params without placeholder are removed.
 */
export function reconcilePathParamsWithUrl(url: string, currentPathParams: ParamDraft[]): ParamDraft[] {
  const placeholderIds = extractPathPlaceholderIdsFromUrl(url)
  const currentById = new Map(
    currentPathParams
      .map((param) => [param.id.trim(), param] as const)
      .filter(([id]) => id.length > 0)
  )

  return placeholderIds.map((id) => {
    const existing = currentById.get(id)
    if (existing) return existing
    return createParamDraft({ id })
  })
}

export function normalizeHeaderDrafts(httpConfig?: MCPAction['http_config']): HeaderDraft[] {
  const requestHeaders = Array.isArray(httpConfig?.request_headers) ? httpConfig?.request_headers : []
  return requestHeaders.map((header, index) => {
    const headerRecord = header as unknown as Record<string, unknown>
    const type = normalizeHeaderValueType(headerRecord)
    const value =
      type === 'secreto'
        ? String(headerRecord.secret_id || headerRecord.value || '')
        : type === 'variable_dinamica'
          ? String(headerRecord.variable_key || headerRecord.variable_name || headerRecord.value || '')
          : String(headerRecord.value || '')
    return createHeaderDraft({
      id: String(headerRecord.id || `hdr_${index}`),
      name: String(headerRecord.name || ''),
      value_type: type,
      value,
    })
  })
}

function normalizeParamValueType(value: unknown): ToolParamValueType {
  if (typeof value === 'string' && PARAM_VALUE_TYPES.includes(value as ToolParamValueType)) {
    return value as ToolParamValueType
  }
  return 'llm_prompt'
}

function normalizeParamType(value: unknown): ToolParamDataType {
  if (typeof value !== 'string') return 'string'
  if (value === 'array' || value === 'object') return 'string'
  if (PARAM_TYPES.includes(value as ToolParamDataType)) {
    return value as ToolParamDataType
  }
  return 'string'
}

function supportsParamEnum(paramType: ToolParamDataType): boolean {
  return paramType === 'string' || paramType === 'number' || paramType === 'integer'
}

export function normalizeParamDrafts(list: unknown): ParamDraft[] {
  if (!Array.isArray(list)) return []
  return list.map((item, index) => {
    const source = item as ToolParamSchemaItem
    const sourceRecord = item as Record<string, unknown>
    return createParamDraft({
      draft_id: String(sourceRecord.draft_id || `param_draft_${index}`),
      id: String(source.id || `param_${index}`),
      type: normalizeParamType(source.type),
      value_type: normalizeParamValueType(source.value_type),
      description: String(source.description || ''),
      required: Boolean(source.required),
      enum: sanitizeEnumValues(source.enum),
      value:
        source.value_type === 'constant'
          ? String(source.constant_value || (source as { value?: string }).value || '')
          : source.value_type === 'dynamic_variable'
            ? String(source.variable_key || source.variable_name || (source as { value?: string }).value || '')
            : '',
    })
  })
}

export function toRequestHeadersPayload(headers: HeaderDraft[]): ToolRequestHeader[] {
  return headers
    .filter((header) => header.name.trim().length > 0)
    .map((header) => {
      const base = {
        id: header.id,
        name: header.name.trim(),
        value_type: header.value_type,
      } as ToolRequestHeader
      if (header.value_type === 'secreto') {
        base.secret_id = header.value.trim()
      } else if (header.value_type === 'variable_dinamica') {
        // `variable_key` es la clave que lee el ejecutor
        // (`http_executor.py:448`). Se escribia `variable_name`, que ahi no
        // existe, asi que una cabecera dinamica creada desde el panel nunca
        // resolvia: el ejecutor la daba por vacia y abortaba la llamada.
        base.variable_key = header.value.trim()
      } else {
        base.value = header.value
      }
      return base
    })
}

export function toParamSchemaPayload(params: ParamDraft[]): ToolParamSchemaItem[] {
  return params
    .filter((param) => param.id.trim().length > 0)
    .map((param) => {
      const enumValues = sanitizeEnumValues(param.enum)
      const payload: ToolParamSchemaItem = {
        id: param.id.trim(),
        type: param.type,
        value_type: param.value_type,
        description: param.description.trim() || undefined,
        required: Boolean(param.required),
        enum:
          supportsParamEnum(param.type) && param.value_type === 'llm_prompt' && enumValues.length > 0
            ? enumValues
            : undefined,
      }
      if (param.value_type === 'constant' && param.value.trim()) {
        payload.constant_value = param.value.trim()
      }
      if (param.value_type === 'dynamic_variable' && param.value.trim()) {
        payload.variable_name = param.value.trim()
      }
      return payload
    })
}

function sanitizeHeaderLabel(headerName: string, index: number): string {
  const trimmed = headerName.trim()
  return trimmed.length > 0 ? trimmed : `#${index + 1}`
}

export function getWebhookHeaderValidationError(headers: HeaderDraft[]): string | null {
  for (let index = 0; index < headers.length; index += 1) {
    const header = headers[index]
    if (header.name.trim().length === 0) continue
    if (header.value_type === 'variable_dinamica' && header.value.trim().length === 0) {
      // Ya no se rechaza el tipo: el backend lo soporta y es lo que usan las
      // herramientas de Mark 2 para mandar `X-Comercial-Id`. Lo que si se exige
      // es el nombre de la variable, porque sin el la llamada aborta.
      return `Header "${sanitizeHeaderLabel(header.name, index)}": indica el nombre de la variable, por ejemplo system.comercial_id.`
    }
    if (header.value_type === 'secreto' && header.value.trim().length === 0) {
      return `Header "${sanitizeHeaderLabel(header.name, index)}": selecciona un secreto.`
    }
  }
  return null
}

function validateParamGroup(params: ParamDraft[], groupLabel: string): string | null {
  for (let index = 0; index < params.length; index += 1) {
    const param = params[index]
    const paramLabel = param.id.trim().length > 0 ? param.id.trim() : `#${index + 1}`
    if (param.id.trim().length === 0) {
      return `Parámetro de ${groupLabel} ${paramLabel}: la clave (id) es obligatoria.`
    }
    if (param.value_type === 'constant' && param.value.trim().length === 0) {
      return `Parámetro de ${groupLabel} "${paramLabel}": el valor constante es obligatorio.`
    }
    if (param.value_type === 'dynamic_variable' && param.value.trim().length === 0) {
      return `Parámetro de ${groupLabel} "${paramLabel}": el nombre de variable es obligatorio.`
    }
    if (Array.isArray(param.enum) && param.enum.length > 0) {
      if (param.enum.some((value) => value.trim().length === 0)) {
        return `Parámetro de ${groupLabel} "${paramLabel}": los valores enum no pueden estar vacíos.`
      }
      if (!supportsParamEnum(param.type)) {
        return `Parámetro de ${groupLabel} "${paramLabel}": enum solo aplica a tipo string, number o integer.`
      }
      if (param.value_type !== 'llm_prompt') {
        return `Parámetro de ${groupLabel} "${paramLabel}": enum solo aplica cuando el origen es LLM Prompt.`
      }
    }
  }
  return null
}

export function getWebhookSchemaValidationError(pathParams: ParamDraft[], queryParams: ParamDraft[]): string | null {
  const pathError = validateParamGroup(pathParams, 'ruta')
  if (pathError) return pathError
  const queryError = validateParamGroup(queryParams, 'consulta')
  if (queryError) return queryError
  return null
}

export function getWebhookParamValidationError(params: ParamDraft[], groupLabel: string): string | null {
  return validateParamGroup(params, groupLabel)
}

export function mapKnownWebhookBackendValidationError(detailText: string): string | null {
  const normalized = detailText.trim()
  if (!normalized) return null

  const missingParamIdMatch = normalized.match(
    /(path_params_schema|query_params_schema|request_body_schema):\s*Item\s*(\d+)\s*requires non-empty 'id'/i
  )
  if (missingParamIdMatch) {
    const groupKey = missingParamIdMatch[1]
    const index = Number(missingParamIdMatch[2])
    const groupLabel = groupKey === 'path_params_schema' ? 'ruta' : groupKey === 'query_params_schema' ? 'consulta' : 'cuerpo'
    if (Number.isFinite(index)) {
      return `Parámetro de ${groupLabel} #${index + 1}: la clave (id) es obligatoria.`
    }
    return `Parámetro de ${groupLabel}: la clave (id) es obligatoria.`
  }

  const secretHeaderMatch = normalized.match(/request_headers:\s*Item\s*(\d+)\s*requires 'secret_id' when type='secret'/i)
  if (secretHeaderMatch) {
    const index = Number(secretHeaderMatch[1])
    if (Number.isFinite(index)) return `Header #${index + 1}: selecciona un secreto.`
  }

  const dynamicHeaderMatch = normalized.match(/request_headers:\s*Item\s*(\d+)\s*uses dynamic headers/i)
  if (dynamicHeaderMatch) {
    const index = Number(dynamicHeaderMatch[1])
    if (Number.isFinite(index)) {
      return `Header #${index + 1}: los headers dinámicos aún no están soportados. Usa "Valor" o "Secreto".`
    }
    return 'Los headers dinámicos aún no están soportados. Usa "Valor" o "Secreto".'
  }

  const constantParamMatch = normalized.match(/(path_params_schema|query_params_schema|request_body_schema):\s*Item\s*(\d+)\s*requires 'value' when value_type='constant'/i)
  if (constantParamMatch) {
    const groupKey = constantParamMatch[1]
    const index = Number(constantParamMatch[2])
    const groupLabel = groupKey === 'path_params_schema' ? 'ruta' : groupKey === 'query_params_schema' ? 'consulta' : 'cuerpo'
    if (Number.isFinite(index)) {
      return `Parámetro de ${groupLabel} #${index + 1}: el valor constante es obligatorio.`
    }
    return `Parámetro de ${groupLabel}: el valor constante es obligatorio.`
  }

  const dynamicParamMatch = normalized.match(/(path_params_schema|query_params_schema|request_body_schema):\s*Item\s*(\d+)\s*requires 'variable_key' when value_type='dynamic_variable'/i)
  if (dynamicParamMatch) {
    const groupKey = dynamicParamMatch[1]
    const index = Number(dynamicParamMatch[2])
    const groupLabel = groupKey === 'path_params_schema' ? 'ruta' : groupKey === 'query_params_schema' ? 'consulta' : 'cuerpo'
    if (Number.isFinite(index)) {
      return `Parámetro de ${groupLabel} #${index + 1}: el nombre de variable es obligatorio.`
    }
    return `Parámetro de ${groupLabel}: el nombre de variable es obligatorio.`
  }

  const enumArrayMatch = normalized.match(/(path_params_schema|query_params_schema|request_body_schema):\s*Item\s*(\d+)\s*'enum' must be an array/i)
  if (enumArrayMatch) {
    const groupKey = enumArrayMatch[1]
    const index = Number(enumArrayMatch[2])
    const groupLabel = groupKey === 'path_params_schema' ? 'ruta' : groupKey === 'query_params_schema' ? 'consulta' : 'cuerpo'
    if (Number.isFinite(index)) {
      return `Parámetro de ${groupLabel} #${index + 1}: enum debe ser una lista de texto.`
    }
    return `Parámetro de ${groupLabel}: enum debe ser una lista de texto.`
  }

  const enumNonEmptyMatch = normalized.match(/(path_params_schema|query_params_schema|request_body_schema):\s*Item\s*(\d+)\s*'enum' value at index\s*(\d+)\s*must be non-empty/i)
  if (enumNonEmptyMatch) {
    const groupKey = enumNonEmptyMatch[1]
    const itemIndex = Number(enumNonEmptyMatch[2])
    const enumIndex = Number(enumNonEmptyMatch[3])
    const groupLabel = groupKey === 'path_params_schema' ? 'ruta' : groupKey === 'query_params_schema' ? 'consulta' : 'cuerpo'
    if (Number.isFinite(itemIndex) && Number.isFinite(enumIndex)) {
      return `Parámetro de ${groupLabel} #${itemIndex + 1}: el valor enum #${enumIndex + 1} no puede estar vacío.`
    }
  }

  const enumTypeMatch = normalized.match(/(path_params_schema|query_params_schema|request_body_schema):\s*Item\s*(\d+)\s*'enum' value at index\s*(\d+)\s*must be a string/i)
  if (enumTypeMatch) {
    const groupKey = enumTypeMatch[1]
    const itemIndex = Number(enumTypeMatch[2])
    const enumIndex = Number(enumTypeMatch[3])
    const groupLabel = groupKey === 'path_params_schema' ? 'ruta' : groupKey === 'query_params_schema' ? 'consulta' : 'cuerpo'
    if (Number.isFinite(itemIndex) && Number.isFinite(enumIndex)) {
      return `Parámetro de ${groupLabel} #${itemIndex + 1}: el valor enum #${enumIndex + 1} debe ser texto.`
    }
  }

  const missingResolvedParamMatch = normalized.match(/Invalid webhook payload resolution:\s*Missing required parameter '([^']+)'/i)
  if (missingResolvedParamMatch) {
    const paramId = missingResolvedParamMatch[1]
    return `Falta resolver el parámetro requerido "${paramId}". Si es de ruta, asegúrate de tener la clave en el parámetro y el segmento {${paramId}} en la URL.`
  }

  const enumResolutionMatch = normalized.match(/Invalid webhook payload resolution:\s*Parameter '([^']+)' must be one of:\s*(.+)$/i)
  if (enumResolutionMatch) {
    const paramId = enumResolutionMatch[1]
    const allowed = enumResolutionMatch[2]
    return `El parámetro "${paramId}" debe ser uno de: ${allowed}.`
  }

  return null
}
